// Leading+trailing throttle that always delivers the LATEST args.
//
// Unlike debounce (which goes silent while events keep arriving and only fires
// after they stop), throttle fires immediately, then at most once per `waitMs`
// for as long as events keep coming, and once more at the end with the final
// args. Use this for continuous streams (e.g. player movement) where the
// server must keep seeing progress DURING the activity — a debounce here let
// players hold a movement key and never report movement until they stopped
// (so fuel never burned and speed never decayed).
export function throttle<A extends unknown[]>(
  fn: (...args: A) => void,
  waitMs: number,
): (...args: A) => void {
  let lastCall = 0;
  let pending: A | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    timer = null;
    if (pending) {
      lastCall = Date.now();
      const args = pending;
      pending = null;
      fn(...args);
    }
  };

  return (...args: A) => {
    const now = Date.now();
    if (timer === null && now - lastCall >= waitMs) {
      lastCall = now;
      fn(...args);
    } else {
      pending = args;
      if (timer === null) {
        timer = setTimeout(flush, Math.max(0, waitMs - (now - lastCall)));
      }
    }
  };
}
